import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatError, fileUrl } from "../../lib/api";
import { Library, Plus, Trash2, School, Users, BookOpen, Package, UploadCloud, X } from "lucide-react";

export default function AdminSchools() {
    const [schools, setSchools] = useState([]);
    const [stats, setStats] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ name: "", branch: "", pin_code: "", spoc: "", email_domain: "" });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        const [s, st] = await Promise.all([api.get("/admin/schools"), api.get("/admin/stats")]);
        setSchools(s.data);
        setStats(st.data);
    };

    useEffect(() => {
        load();
    }, []);

    const pickLogo = (f) => {
        setLogoFile(f);
        setLogoPreview(f ? URL.createObjectURL(f) : "");
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let logo_file_id = null;
            if (logoFile) {
                const fd = new FormData();
                fd.append("file", logoFile);
                const up = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                logo_file_id = up.data.file_id;
            }
            await api.post("/admin/schools", { ...form, logo_file_id });
            toast.success(`School "${form.name}" registered`);
            setForm({ name: "", branch: "", pin_code: "", spoc: "", email_domain: "" });
            setLogoFile(null); setLogoPreview("");
            setShowForm(false);
            await load();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setLoading(false);
        }
    };

    const remove = async (id, name) => {
        if (!window.confirm(`Delete school "${name}"? This cannot be undone.`)) return;
        try {
            await api.delete(`/admin/schools/${id}`);
            toast.success(`School "${name}" deleted`);
            await load();
        } catch (e) {
            toast.error(formatError(e));
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-10">
            <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
                <div>
                    <div className="label-mini">Admin</div>
                    <h1 className="font-serif text-4xl font-semibold">Schools on Zapperus</h1>
                    <p className="text-ink/60 mt-2">Onboard new schools. Each school gets its own verified email domain.</p>
                </div>
                <button data-testid="admin-add-school-btn" onClick={() => setShowForm(true)} className="btn-primary">
                    <Plus className="h-4 w-4" /> Add school
                </button>
            </div>

            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                    <StatCard icon={<School className="h-4 w-4" />} label="Schools" value={stats.schools} />
                    <StatCard icon={<Users className="h-4 w-4" />} label="Students" value={stats.students} />
                    <StatCard icon={<BookOpen className="h-4 w-4" />} label="Books listed" value={stats.books} />
                    <StatCard icon={<Package className="h-4 w-4" />} label="Active reservations" value={stats.active_reservations} />
                </div>
            )}

            {showForm && (
                <div className="bg-white border border-parchment rounded-2xl p-6 mb-8 shadow-sm">
                    <h2 className="font-serif text-2xl font-semibold mb-4">Register a new school</h2>
                    <form onSubmit={submit} className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <label className="label-mini">School logo (optional)</label>
                            <label className="w-full border-2 border-dashed border-parchment rounded-xl p-5 flex items-center gap-4 cursor-pointer hover:bg-paper transition-colors" data-testid="school-logo-label">
                                {logoPreview ? (
                                    <img src={logoPreview} alt="Logo preview" className="h-16 w-16 rounded-lg object-contain border border-parchment bg-white" />
                                ) : (
                                    <div className="h-16 w-16 rounded-lg bg-paper border border-parchment flex items-center justify-center">
                                        <UploadCloud className="h-6 w-6 text-ink/40" />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="text-sm font-semibold">{logoFile ? logoFile.name : "Click to upload logo"}</div>
                                    <div className="text-xs text-ink/50 mt-0.5">PNG or JPG · up to 5MB · shown on the home page</div>
                                </div>
                                {logoFile && (
                                    <button type="button" onClick={(e) => { e.preventDefault(); pickLogo(null); }} className="p-2 rounded-full hover:bg-parchment" data-testid="school-logo-remove">
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                                <input data-testid="school-logo-input" type="file" accept="image/*" onChange={(e) => pickLogo(e.target.files[0])} className="hidden" />
                            </label>
                        </div>
                        <Field label="School name" testid="school-name-input" value={form.name} onChange={(v) => setForm({...form, name: v})} placeholder="Sunrise Public School" />
                        <Field label="Branch" testid="school-branch-input" value={form.branch} onChange={(v) => setForm({...form, branch: v})} placeholder="Whitefield" />
                        <Field label="Pin code" testid="school-pin-input" value={form.pin_code} onChange={(v) => setForm({...form, pin_code: v})} placeholder="560066" />
                        <Field label="SPOC" testid="school-spoc-input" value={form.spoc} onChange={(v) => setForm({...form, spoc: v})} placeholder="Ms. Aparna Rao — aparna@sunrise.edu" />
                        <div className="md:col-span-2">
                            <label className="label-mini">Email domain</label>
                            <div className="flex items-center bg-paper border-b-2 border-parchment focus-within:border-brick rounded-t-md px-4">
                                <span className="text-ink/50 pr-1 font-mono">@</span>
                                <input data-testid="school-domain-input" required value={form.email_domain} onChange={(e) => setForm({...form, email_domain: e.target.value})} className="flex-1 py-3 bg-transparent focus:outline-none font-mono" placeholder="sunrise.edu" />
                            </div>
                            <p className="text-xs text-ink/50 mt-2">Only students with an email on this domain will be able to register for this school.</p>
                        </div>
                        <div className="md:col-span-2 flex items-center gap-3 justify-end pt-2">
                            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
                            <button data-testid="school-submit-btn" type="submit" disabled={loading} className="btn-primary">
                                {loading ? "Saving…" : "Register school"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {schools.length === 0 ? (
                <div className="bg-white border-2 border-dashed border-parchment rounded-2xl p-12 text-center">
                    <div className="h-14 w-14 mx-auto rounded-full bg-paper flex items-center justify-center mb-4">
                        <Library className="h-6 w-6 text-ink/40" />
                    </div>
                    <div className="font-serif text-xl mb-1">No schools yet</div>
                    <p className="text-sm text-ink/60 mb-4">Add your first school to allow students to register.</p>
                    <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="admin-empty-add-btn">
                        <Plus className="h-4 w-4" /> Add school
                    </button>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {schools.map((s) => (
                        <div key={s.id} data-testid={`admin-school-card-${s.id}`} className="bg-white border border-parchment rounded-2xl p-5 shadow-sm hover:shadow-book transition-shadow">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3 min-w-0">
                                    {s.logo_file_id ? (
                                        <img src={fileUrl(s.logo_file_id)} alt={s.name} className="h-12 w-12 rounded-lg object-contain border border-parchment bg-white flex-shrink-0" />
                                    ) : (
                                        <div className="h-12 w-12 rounded-lg bg-paper border border-parchment flex items-center justify-center flex-shrink-0 text-ink/40 font-serif font-bold">
                                            {s.name?.[0]?.toUpperCase() || "S"}
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <div className="font-serif text-xl font-semibold truncate">{s.name}</div>
                                        <div className="text-sm text-ink/60">{s.branch} · Pin {s.pin_code}</div>
                                    </div>
                                </div>
                                <button data-testid={`admin-school-delete-${s.id}`} onClick={() => remove(s.id, s.name)} className="p-2 rounded-full text-red-600 hover:bg-red-50 transition-colors flex-shrink-0" title="Delete school">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="mt-4 flex items-center gap-2 flex-wrap">
                                <span className="inline-flex px-2.5 py-1 rounded-md bg-paper border border-parchment text-xs font-mono text-ink/70">@{s.email_domain}</span>
                                <span className="inline-flex px-2.5 py-1 rounded-md bg-library/10 text-library text-xs font-semibold">{s.student_count} students</span>
                            </div>
                            <div className="mt-3 text-xs text-ink/50">
                                <span className="uppercase tracking-widest font-bold">SPOC · </span>{s.spoc}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function StatCard({ icon, label, value }) {
    return (
        <div className="bg-white border border-parchment rounded-2xl p-5">
            <div className="flex items-center gap-2 text-ink/60 mb-2">
                {icon}
                <span className="text-xs uppercase tracking-widest font-bold">{label}</span>
            </div>
            <div className="font-serif text-3xl font-semibold">{value}</div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, testid }) {
    return (
        <div>
            <label className="label-mini">{label}</label>
            <input data-testid={testid} required value={value} onChange={(e) => onChange(e.target.value)} className="input-field" placeholder={placeholder} />
        </div>
    );
}
